import { statusLabel, statusTone } from '@clim/client-core';
import type { DossierStatus } from '@clim/domain';
import { useEffect, useRef, type ReactNode, type InputHTMLAttributes, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';

export function StatusBadge({ status }: { status: DossierStatus }) {
  return <span className={`badge tone-${statusTone(status)}`}>{statusLabel(status)}</span>;
}

export function Field({ label, error, children, hint }: { label: string; error?: string; hint?: string; children: ReactNode }) {
  return (
    <label className={`field${error ? ' has-error' : ''}`}>
      <span className="field-label">{label}</span>
      {children}
      {hint && !error && <span className="field-hint">{hint}</span>}
      {error && <span className="field-error" role="alert">{error}</span>}
    </label>
  );
}

export const Input = (p: InputHTMLAttributes<HTMLInputElement>) => <input className="input" {...p} />;
export const Textarea = (p: TextareaHTMLAttributes<HTMLTextAreaElement>) => <textarea className="input" rows={3} {...p} />;
export const Select = (p: SelectHTMLAttributes<HTMLSelectElement>) => <select className="input" {...p} />;

export function Modal({ title, onClose, children }: { title: string; onClose(): void; children: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    ref.current?.showModal();
  }, []);
  return (
    <dialog ref={ref} className="modal" onClose={onClose} aria-label={title}>
      <header className="modal-head">
        <h2>{title}</h2>
        <button className="btn ghost" onClick={onClose} aria-label="Fermer">
          ✕
        </button>
      </header>
      {children}
    </dialog>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="empty">{children}</p>;
}

export function ErrorBox({ message }: { message: string | null }) {
  return message ? (
    <p className="error-box" role="alert">
      {message}
    </p>
  ) : null;
}

/** « À synchroniser » visible sans bloquer l'utilisateur (CDC §23). */
export function SyncTag({ state }: { state: 'synced' | 'pending' | 'syncing' }) {
  if (state === 'synced') return null;
  return (
    <span className="sync-tag" title="Modification locale non encore envoyée au serveur">
      {state === 'syncing' ? '⟳ Envoi…' : '⟳ À synchroniser'}
    </span>
  );
}

export function PendingDot({ show }: { show: boolean }) {
  return show ? <span className="pending-dot" title="En attente de synchronisation" aria-label="En attente de synchronisation" /> : null;
}
